import { useEffect, useRef, useState } from 'react';
import { postSearch } from '../api/search';
import { ApiClientError } from '../api/client';
import MarkdownView from './MarkdownView';

type SearchState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ok'; markdown: string }
  | { kind: 'rate-limited'; message: string; retryAfterSeconds: number }
  | { kind: 'timeout'; message: string }
  | { kind: 'error'; message: string };

export default function SearchBox() {
  const [query, setQuery] = useState('');
  const [state, setState] = useState<SearchState>({ kind: 'idle' });
  const [countdown, setCountdown] = useState(0);
  const controllerRef = useRef<AbortController | null>(null);
  const formRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (state.kind !== 'rate-limited' || countdown <= 0) return;
    const id = setTimeout(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(id);
  }, [state, countdown]);

  // Click-outside to dismiss the popover.
  useEffect(() => {
    if (state.kind === 'idle' || state.kind === 'loading') return;
    function onClick(e: MouseEvent) {
      if (formRef.current && !formRef.current.contains(e.target as Node)) {
        setState({ kind: 'idle' });
      }
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [state.kind]);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!query.trim()) return;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setState({ kind: 'loading' });
    postSearch(query, controller.signal)
      .then((resp) => setState({ kind: 'ok', markdown: resp.markdown }))
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (err instanceof ApiClientError) {
          if (err.status === 429) {
            const retry = err.error.retryAfterSeconds ?? 60;
            setCountdown(retry);
            setState({ kind: 'rate-limited', message: err.message, retryAfterSeconds: retry });
            return;
          }
          if (err.status === 504) return setState({ kind: 'timeout', message: err.message });
          return setState({ kind: 'error', message: err.message });
        }
        setState({ kind: 'error', message: String(err) });
      });
  }

  return (
    <div ref={formRef} data-testid="search-box" style={{ position: 'relative', width: '100%' }}>
      <form onSubmit={submit} className="search-form">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Ask the wiki..."
          data-testid="search-input"
          aria-label="Search query"
        />
        <button type="submit" data-testid="search-submit-button">
          Search
        </button>
      </form>

      {state.kind !== 'idle' ? (
        <div className="search-popover">
          {state.kind === 'loading' ? (
            <div className="search-status" data-testid="search-loading">
              Searching the wiki...
            </div>
          ) : null}
          {state.kind === 'ok' ? (
            <MarkdownView markdown={state.markdown} testId="search-result-content" />
          ) : null}
          {state.kind === 'rate-limited' ? (
            <div className="search-status search-status--warning" data-testid="search-rate-limit-message" role="status">
              <strong>Rate limit reached.</strong> {state.message}
              {countdown > 0 ? <div className="muted">Retry in {countdown}s.</div> : <div className="muted">Try again now.</div>}
            </div>
          ) : null}
          {state.kind === 'timeout' ? (
            <div className="search-status search-status--warning" data-testid="search-timeout-message" role="status">
              <strong>Search timed out.</strong> {state.message}
            </div>
          ) : null}
          {state.kind === 'error' ? (
            <div className="search-status search-status--error" data-testid="search-error-message" role="alert">
              <strong>Search failed.</strong> {state.message}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
